import { type ReactNode, useState } from "react";

export interface PaneTab {
  id: string;
  label: string;
  content: ReactNode;
}

interface PaneProps {
  label: string;
  testId: string;
  tabs: PaneTab[];
  /** The selected tab, when the parent controls it (e.g. to jump to the Timeline). */
  active?: string;
  onActive?: (id: string) => void;
}

/** A panel with a tab bar (docs/ui-design.md §1); a single tab renders as a title. */
export function Pane({ label, testId, tabs, active: controlled, onActive }: PaneProps) {
  const [own, setOwn] = useState(tabs[0]?.id);
  const active = controlled ?? own;
  const setActive = (id: string) => {
    setOwn(id);
    onActive?.(id);
  };
  const current = tabs.find((tab) => tab.id === active) ?? tabs[0];
  return (
    <section aria-label={label} data-testid={testId} className="flex min-h-0 flex-1 flex-col">
      <div
        role="tablist"
        aria-label={label}
        className="flex gap-1 border-b border-border bg-bg-dark px-2 text-xs font-semibold uppercase tracking-wide"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${testId}-tab-${tab.id}`}
            aria-selected={tab.id === current?.id}
            aria-controls={`${testId}-panel`}
            onClick={() => setActive(tab.id)}
            className={`border-b-2 px-2 py-1 ${
              tab.id === current?.id
                ? "border-blue text-fg"
                : "border-transparent text-fg-dark hover:text-fg"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div
        id={`${testId}-panel`}
        role="tabpanel"
        aria-labelledby={`${testId}-tab-${current?.id}`}
        className="min-h-0 flex-1 overflow-auto"
      >
        {current?.content}
      </div>
    </section>
  );
}
